import Server from './server';
import moment from 'moment';

import ComponentFunction from "./models/ComponentFunction";
import ComponentHook from "./models/ComponentHook";
import Component from "./models/Component";
import ComponentParameter from "./models/ComponentParameter";
import ComponentParameters from "./models/ComponentParameters";
import ComponentTest from "./models/ComponentTest";
import ComponentEnvVariable from "./models/ComponentEnvVariable";
import Dataset from "./models/Dataset";
import DatasetCell from "./models/DatasetCell";
import DatasetColumn from "./models/DatasetColumn";
import DatasetRow from "./models/DatasetRow";
import Document from "./models/Document";
import Link from "./models/Link";
import Model from "./models/Model";
import Page from "./models/Page";
import Response from "./models/Response";
import Strategy from "./models/Strategy";
import Assembly from "./models/Assembly";
import AssemblyParameters from "./models/AssemblyParameters";
import AssemblyParametersStructure from "./models/AssemblyParametersStructure";
import Session from "./models/Session";
import SessionStats from "./models/SessionStats";
import SessionStructure from "./models/SessionStructure";
import Transaction from "./models/Transaction";
import User from "./models/User";

import SpeechSynthesis from "./utils/SpeechSynthesis";
import Shortcuts from "./utils/Shortcuts";
import Rules from "./utils/Rules";
import EventBus from "./utils/EventBus";
import File from "./utils/File";
import Hash from "./utils/Hash";
import Color from "./utils/Color";
import Device from "./utils/Device";
import Cookies from "./utils/Cookies";
import Helpers from "./utils/Helpers";

import ActivityService from './services/ActivityService';
import AuthService from './services/AuthService';
import CommentService from './services/CommentService';
import ComponentService from './services/ComponentService';
import DatasetService from './services/DatasetService';
import DeploymentService from './services/DeploymentService';
import DocumentService from './services/DocumentService';
import FileService from './services/FileService';
import FollowingService from './services/FollowingService';
import FormService from './services/FormService';
import DocumentationService from './services/DocumentationService';
import HistoryService from './services/HistoryService';
import LanguageService from './services/LanguageService';
import LikeService from './services/LikeService';
import LinkService from './services/LinkService';
import MessagingService from './services/MessagingService';
import NewsService from './services/NewsService';
import NotificationService from './services/NotificationService';
import PageService from './services/PageService';
import PostService from './services/PostService';
import SearchService from './services/SearchService';
import ServerService from './services/ServerService';
import SettingsService from './services/SettingsService';
import StatsService from './services/StatsService';
import StrategyService from './services/StrategyService';
import SessionService from './services/SessionService';
import SessionStatsService from './services/SessionStatsService';
import SessionStructureService from './services/SessionStructureService';
import TransactionService from './services/TransactionService';
import UserService from './services/UserService';

import LANGUAGES from './constants/languages';
import ICONS from './constants/icons';
import THEME from './constants/theme';

const sounds = {
	beep: new Audio('https://polymind.s3.ca-central-1.amazonaws.com/sdk/sounds/beep.ogg'),
	start: new Audio('https://polymind.s3.ca-central-1.amazonaws.com/sdk/sounds/start.ogg'),
	good: new Audio('https://polymind.s3.ca-central-1.amazonaws.com/sdk/sounds/good.ogg'),
	review: new Audio('https://polymind.s3.ca-central-1.amazonaws.com/sdk/sounds/review.ogg'),
	bad: new Audio('https://polymind.s3.ca-central-1.amazonaws.com/sdk/sounds/bad.ogg'),
	stash: new Audio('https://polymind.s3.ca-central-1.amazonaws.com/sdk/sounds/stash.ogg'),
	completed: new Audio('https://polymind.s3.ca-central-1.amazonaws.com/sdk/sounds/completed.ogg'),
	undo: new Audio('https://polymind.s3.ca-central-1.amazonaws.com/sdk/sounds/undo.ogg'),
};

export {
	ComponentFunction,
	ComponentHook,
	Component,
	ComponentParameter,
	ComponentParameters,
	ComponentTest,
	ComponentEnvVariable,
	Dataset,
	DatasetCell,
	DatasetColumn,
	DatasetRow,
	Document,
	Link,
	Model,
	Page,
	Response,
	Strategy,
	Assembly,
	AssemblyParameters,
	AssemblyParametersStructure,
	Session,
	SessionStats,
	SessionStructure,
	Transaction,
	User,

	SpeechSynthesis,
	Shortcuts,

	Rules,
	EventBus,
	File,
	Hash,
	Color,
	Device,
	Cookies,
	Helpers,

	ActivityService,
	AuthService,
	CommentService,
	ComponentService,
	DatasetService,
	DeploymentService,
	DocumentService,
	FileService,
	FollowingService,
	FormService,
	DocumentationService,
	HistoryService,
	LanguageService,
	LikeService,
	LinkService,
	MessagingService,
	NewsService,
	NotificationService,
	PageService,
	PostService,
	SearchService,
	ServerService,
	SettingsService,
	StatsService,
	StrategyService,
	SessionService,
	SessionStatsService,
	SessionStructureService,
	TransactionService,
	UserService,

	LANGUAGES,
	ICONS,
	THEME,
}

let parameters = {};
const subscriptions = {};
const shortcuts = new Shortcuts();
shortcuts.attach(document.body);

window.addEventListener('message', message => {
	if (message.data) {
		try {
			const event = JSON.parse(message.data);
			if (Array.isArray(subscriptions[event.type])) {

				if (['init', 'parameters'].indexOf(event.type) !== -1) {
					Cookies.set('polymind-dark', event.data.general.dark);
					Cookies.set('polymind-speech', event.data.general.speech);
					Cookies.set('polymind-sound', event.data.general.sound);
				}

				subscriptions[event.type].forEach(callback => callback(event.data));
			}
		} catch(e) {

		}
	}
});

export default class PolymindSDK {

	constructor() {

		this.context = null;
		this.contextStat = null;
		this.theme = THEME;

		Server.refreshIfNeeded();
	}

	addShortcut(name, desc, keys, callback) {
		shortcuts.add(name, desc, undefined, keys, callback);
		this.emit('shortcut', shortcuts.list);
	}

	removeShortcut(callback) {
		shortcuts.remove(callback);
		this.emit('remove_shortcut', shortcuts.list);
	}

	playSound(name) {

		if (Cookies.get('polymind-sound') === 'false') {
			return;
		}

		if (!sounds[name]) {
			throw new Error('This sound name does\'t exists.');
		}
		sounds[name].currentTime = 0;
		sounds[name].play();
	}

	getStrategy(id) {
		return Server.getItem('strategy', id, {
			fields: '*'
		}).then(response => {
			return new Strategy(response.data);
		});
	}

	getStrategies() {
		return Server.getItems('strategy', {
			fields: '*'
		}).then(response => {
			return response.data.map(strategy => new Strategy(response.data));
		});
	}

	getComponent(id) {
		return Server.getItem('component', id, {
			fields: '*'
		}).then(response => {
			return new Component(response.data);
		});
	}

	getComponents() {
		return Server.getItems('component', {
			fields: '*'
		}).then(response => {
			return response.data.map(component => new Component(response.data));
		});
	}

	getDataset(id) {
		return Server.getItem('dataset', id, {
			fields: '*,rows.*,rows.cells.*,columns.*'
		}).then(response => {
			const dataset = new Dataset(response.data);
			if (dataset.is_remote) {
				return dataset.getRemote();
			}
			return dataset.clean();
		});
	}

	getDatasets() {
		return Server.getItems('dataset', {
			fields: '*,columns.*'
		}).then(response => {
			return response.data.map(dataset => new Dataset(dataset));
		});
	}

	getColumn(dataset, guid) {
		return dataset.columns.find(column => column.guid === guid);
	}

	getColumns(dataset, parameters) {
		const columns = {};
		for(let parameter in parameters) {
			const guid = parameters[parameter];
			const column = this.getColumn(dataset, guid);
			if (column) {
				column.getIndex = () => {
					return dataset.columns.findIndex(column => column.guid === guid);
				};
				columns[parameter] = column;
			}
		}
		return columns;
	}

	getData(dataset, columnIdx, rowIdx) {
		return dataset.rows[rowIdx].cells[columnIdx];
	}

	me() {
		return UserService.me().then(response => {
			return new User(response.data);
		});
	}

	emit(type, settings, win = window.parent) {
		win.postMessage(JSON.stringify({
			type: type,
			data: settings,
		}), '*');
	}

	subscribe(type, callback) {
		if (!Array.isArray(subscriptions[type])) {
			subscriptions[type] = [];
		}

		subscriptions[type].push(callback);
	}

	init(settings) {
		this.subscribe('init', settings.onInit);
		this.subscribe('go', settings.onGo);
		this.subscribe('parameters', settings.onParamUpdate);
		this.subscribe('shortcut', shortcut => {
			shortcuts.run(shortcut.arrKeys);
		});

		const eventCallback = () => {
			this.emit('event');
		};
		document.addEventListener('mousemove', eventCallback);
		document.addEventListener('keydown', eventCallback);
	}

	setContext(item, start = false) {

		if (this.contextStat !== null && !this.contextStat.pushed) {
			this.contextStat.push('viewed');
		}

		this.context = item;
		this.emit('context', item);

		if (start) {
			return this.initContextStat();
		}
	}

	initContextStat() {

		const showed_on = moment().format('YYYY-MM-DD HH:mm:ss');
		const context = {
			guid: Hash.guid(),
			pushed: false,
			context: this.context.id,
			undo: () => {

				if (!context.pushed) {
					return false;
				}

				this.emit('undo', context.guid);
			},
			push: (tag, meta) => {

				if (context.pushed) {
					return false;
				}

				const stat = {
					guid: context.guid,
					collection: 'dataset_cell',
					item_id: this.context.id,
					showed_on,
					answered_on: moment().format('YYYY-MM-DD HH:mm:ss'),
					tag, meta
				};

				this.emit('stat', stat);

				context.pushed = true;

				return true;
			}
		};

		this.contextStat = context;
		return context;
	}

	deployStats(stats, sessionId) {
		const transactions = [];
		stats.forEach(stat => {
			transactions.push({
				action: 'insert',
				collection: 'session_stats',
				data: {
					session: sessionId,
					...stat
				}
			});
		});

		return SessionStatsService.push(transactions).then(() => {

		});
	}

	getContext() {
		return this.context;
	}

	login(email, password) {
		return Server.login({
			email,
			password
		});
	}

	logout() {
		return new Promise((resolve, reject) => {
			Server.logout();
			resolve();
		});
	}

	isLoggedIn() {
		return Server.isLoggedIn();
	}

	ping() {
		return Server.ping();
	}

	read(text, settings) {
		this.emit('read', {
			text,
			settings,
		});
	}
}
